import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useCtxConfig} from 'pages/dashboard/components/FirstLook/context';
import {ScreenConfig} from 'pages/dashboard/components/FirstLook/types';

import * as styles from './styles.module.scss';

const INTERVAL_MS = 100;
const DEFAULT_DISPLAY_MS = INTERVAL_MS * 50;

const getElapsed = (target: number, remaining: number) => {
	return ((target - remaining) / target) * 100;
};

export const Progress = () => {
	const {state, dispatch, sdelay} = useCtxConfig();
	const [elapsed, setElapsed] = useState(0);
	const autoSwipe = sdelay && sdelay > 0;
	const displayDelay = autoSwipe ? sdelay : DEFAULT_DISPLAY_MS;

	const indexRef = useRef(state.index);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const lastTickRef = useRef<number | null>(null);
	const remainingRef = useRef<number>(displayDelay);

	const stopTimer = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
			lastTickRef.current = null;
		}
	}, []);

	// Track index changes and reset timer & elapsed as needed
	useEffect(() => {
		if (state.index !== indexRef.current) {
			stopTimer();
			remainingRef.current = displayDelay;
			setElapsed(0);
			indexRef.current = state.index;
		}
	}, [state.index]);

	// Handle pause and resume of the timer
	useEffect(() => {
		if (state.isPaused) {
			if (intervalRef.current && lastTickRef.current) {
				const timeDelta = Date.now() - lastTickRef.current;
				remainingRef.current = Math.max(0, remainingRef.current - timeDelta);
				setElapsed(getElapsed(displayDelay, remainingRef.current));
			}
			stopTimer();
			return;
		}

		const shouldRun = !state.isPaused &&
			state.index >= 0 &&
			state.index < state.screens.length &&
			!intervalRef.current &&
			remainingRef.current > 0;

		if (shouldRun) {
			lastTickRef.current = Date.now();

			intervalRef.current = setInterval(() => {
				const now = Date.now();
				const timeDelta = now - (lastTickRef.current ?? now);
				lastTickRef.current = now;

				// If we're already at 0, exit
				if (remainingRef.current <= 0) {
					return;
				}

				remainingRef.current = Math.max(0, remainingRef.current - timeDelta);
				setElapsed(getElapsed(displayDelay, remainingRef.current));

				// We're newly at 0, transition
				if (remainingRef.current <= 0) {
					stopTimer();
					if (autoSwipe) {
						dispatch({type: 'NEXT_SCREEN'});
					}
				}
			}, INTERVAL_MS);
		}

		return stopTimer;

	}, [state.isPaused, state.index, state.screens.length]);

	if (!state.screens.length) {
		return null;
	}

	return (
		<div className={styles.progress}>
			{state.screens.map((screen: ScreenConfig, i: number) => {
				const isActive = i === state.index;
				const isCompleted = i < state.index;
				let width = '0%';

				if (isCompleted) {
					width = '100%';
				} else if (isActive) {
					const displayPercent = Math.max(0, Math.min(100, elapsed));
					width = `${isNaN(displayPercent) ? 0 : displayPercent}%`;
				}

				return (
					<div key={screen.id} className={styles.bar}>
						<div
							className={styles.filled}
							style={{width}}
						/>
					</div>
				);
			})}
		</div>
	);
};
